import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import Loader from "src/components/common/loader/loader";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { launchActions, launchStore } from "src/modules/engagement/launchStore";
import { workflowEvents } from "src/modules/engagement/workflow/workflowEvents";
import { EngagementService } from "@services/engagements/engagementService";
import { LaunchNetworkService } from "@services/engagements/launch/launchNetworkService";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { fetchReportingNetworkConfigs } from "@services/referenceData/reportingNetworkConfigService";
import type { Launch } from "@models/launch.model";
import type { PopulationSegment } from "@models/populationSegment.model";
import CampaignList, { objectiveNameFor, type ObjectiveOption } from "./CampaignList";
import CampaignDetail from "./CampaignDetail";
import { useItemRoute } from "../../../workflow/useItemRoute";
import CampaignFormModal from "./CampaignFormModal";
import ObjectiveFormModal from "./ObjectiveFormModal";
import ObjectiveDetail from "./ObjectiveDetail";
import "./campaign.scss";

async function fetchCampaignsWithPublishers(engagementId: number) {
  const loaded = await new EngagementService().getEngagementLaunches(engagementId);
  const service = new LaunchNetworkService();
  const perCampaign = await Promise.all(
    loaded.map(async (campaign) => {
      const configs = await service.getConfigs(campaign.id).catch(() => []);
      const live = configs.filter(
        (c) => c.is_enabled && c.network_resource_id && c.network_template_id,
      );
      return [campaign.id, live.map((c) => c.network)] as const;
    }),
  );
  return {
    loaded,
    livePublisherCountById: Object.fromEntries(
      perCampaign.map(([id, networks]) => [id, networks.length]),
    ) as Record<number, number>,
    publisherNetworksById: Object.fromEntries(perCampaign) as Record<number, string[]>,
  };
}

export default function CampaignStep() {
  const { selectedEngagement } = useSnapshot(engagementStore);
  const engagementId = selectedEngagement?.id ?? 0;
  const clientOrg = useMemo(
    () => ({ id: selectedEngagement?.client?.id ?? 0, name: selectedEngagement?.client?.name ?? "" }),
    [selectedEngagement?.client?.id, selectedEngagement?.client?.name],
  );

  const [campaigns, setCampaigns] = useState<Launch[]>([]);
  const [objectives, setObjectives] = useState<ObjectiveOption[]>([]);
  const [segments, setSegments] = useState<PopulationSegment[]>([]);
  const [openCampaignId, setOpenCampaignId] = useItemRoute();
  /*
      The objective lives on a query param rather than the item slot: that slot
      already holds a campaign id, and two kinds of id in one position could not
      be told apart on a page reload.
  */
  const [search, setSearch] = useSearchParams();
  const objectiveParam = Number(search.get("objective"));
  const openObjectiveId = Number.isFinite(objectiveParam) && objectiveParam > 0 ? objectiveParam : null;
  const setOpenObjectiveId = useCallback(
    (id: number | null) => {
      setSearch(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id == null) next.delete("objective");
          else next.set("objective", String(id));
          return next;
        },
        { replace: false },
      );
    },
    [setSearch],
  );
  const [livePublisherCountById, setLivePublisherCountById] = useState<Record<number, number>>({});
  const [publisherNetworksById, setPublisherNetworksById] = useState<Record<number, string[]>>({});
  const [networkLabelByKey, setNetworkLabelByKey] = useState<Record<string, string>>({});
  // Which objective the new campaign will belong to — null when the engagement
  // has none, which is allowed: the group is then just a label.
  const [creatingFor, setCreatingFor] = useState<{ id: number | null; name: string } | null>(null);
  const [isCreatingObjective, setIsCreatingObjective] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!engagementId) return;
    let cancelled = false;
    setError(null);
    setIsLoading(true);
    fetchCampaignsWithPublishers(engagementId)
      .then(({ loaded, livePublisherCountById: counts, publisherNetworksById: networks }) => {
        if (cancelled) return;
        setCampaigns(loaded);
        setLivePublisherCountById(counts);
        setPublisherNetworksById(networks);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load campaigns.");
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [engagementId]);

  /*
      Objectives are what the list groups by, and what a new campaign is tied
      to. An objective with no name still groups — the label falls back to a
      string rather than dropping the campaign out of the list.
  */
  useEffect(() => {
    if (!engagementId) return;
    let cancelled = false;
    new EngagementService()
      .getObjectives(engagementId)
      .then((loaded: { id: number; name?: string }[]) => {
        if (cancelled) return;
        setObjectives(
          (loaded ?? []).map((objective) => ({
            id: objective.id,
            name: objective.name?.trim() || `Objective #${objective.id}`,
          })),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setObjectives([]);
      });
    return () => {
      cancelled = true;
    };
  }, [engagementId]);

  useEffect(() => {
    if (!clientOrg.id) return;
    new OrganizationService()
      .getPopulationSegments(clientOrg.id)
      .then(setSegments)
      .catch(() => undefined);
  }, [clientOrg.id]);

  useEffect(() => {
    fetchReportingNetworkConfigs()
      .then((configs) => {
        setNetworkLabelByKey(
          // Skip direct delivery channels: they carry no publisher network, and this map is
          // keyed by one.
          Object.fromEntries(
            configs
              .filter((c) => c.publisher_network)
              .map((c) => [c.publisher_network!.toLowerCase(), c.label]),
          ),
        );
      })
      .catch(() => undefined);
  }, []);

  /*
      Which view shows is the URL's business alone: a campaign id in the route
      means the detail, no id means the list. The step used to auto-open the
      sole campaign behind a ref that reset on every mount, so the same click
      on Campaign landed on the detail or the list depending on whether the
      user had backed out earlier in the visit.
  */
  const openCampaign = campaigns.find((c) => c.id === openCampaignId) ?? null;

  // Other phases and the Audience step read the selected campaign from the stores.
  useEffect(() => {
    if (!openCampaign) return;
    engagementStore.selectedLaunch = openCampaign;
    launchActions.setCurrentLaunch(openCampaign.id);
    launchStore.isLoadingNetworks = true;
    new LaunchNetworkService()
      .getConfigs(openCampaign.id)
      .then(launchActions.setNetworkConfigs)
      .catch(() => setError("Could not load publishers for this campaign."))
      .finally(() => {
        launchStore.isLoadingNetworks = false;
      });
     
  }, [openCampaign?.id]);

  const handleCreate = async (
    name: string,
    populationSegmentId: number | null,
    explicitNone: boolean,
  ) => {
    if (!creatingFor) return;
    setIsSaving(true);
    setError(null);
    try {
      const created = await new EngagementService().createEngagementLaunch(
        engagementId,
        name,
        creatingFor.id ?? undefined,
        populationSegmentId,
        explicitNone,
      );
      setCreatingFor(null);
      const { loaded, livePublisherCountById: counts, publisherNetworksById: networks } =
        await fetchCampaignsWithPublishers(engagementId);
      setCampaigns(loaded);
      setLivePublisherCountById(counts);
      setPublisherNetworksById(networks);
      setOpenCampaignId(created.id);
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } catch {
      setCreatingFor(null);
      setError("Could not create the campaign. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateObjective = async (name: string) => {
    setIsSaving(true);
    setError(null);
    try {
      const created: { id: number; name?: string } = await new EngagementService().createObjective(
        engagementId,
        name,
      );
      setObjectives((prev) => [...prev, { id: created.id, name: created.name?.trim() || name }]);
      setIsCreatingObjective(false);
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } catch {
      setError("Could not create the objective. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleObjectiveRenamed = (objectiveId: number, name: string) => {
    setObjectives((prev) => prev.map((o) => (o.id === objectiveId ? { ...o, name } : o)));
  };

  const handleCampaignChanged = (updated: Launch) => {
    setCampaigns((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    engagementStore.selectedLaunch = updated;
  };

  if (isLoading) return <Loader />;

  return (
    <>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {openObjectiveId != null && !openCampaign ? (
        <ObjectiveDetail
          engagementId={engagementId}
          objectiveId={openObjectiveId}
          objectiveName={
            objectives.find((o) => o.id === openObjectiveId)?.name ?? `Objective #${openObjectiveId}`
          }
          campaigns={campaigns.filter((c) => c.objective_id === openObjectiveId)}
          onBack={() => setOpenObjectiveId(null)}
          onOpenCampaign={(campaignId) => {
            setOpenObjectiveId(null);
            setOpenCampaignId(campaignId);
          }}
          onRenamed={handleObjectiveRenamed}
        />
      ) : openCampaign ? (
        <CampaignDetail
          campaign={openCampaign}
          clientOrg={clientOrg}
          segments={segments}
          objectiveName={objectiveNameFor(openCampaign, objectives)}
          onBack={() => setOpenCampaignId(null)}
          onCampaignChanged={handleCampaignChanged}
        />
      ) : (
        // A failed load already has its own banner above; don't also claim
        // there are zero campaigns — that's not known to be true.
        !error || campaigns.length > 0 ? (
          <CampaignList
            campaigns={campaigns}
            objectives={objectives}
            segments={segments}
            livePublisherCountById={livePublisherCountById}
            publisherNetworksById={publisherNetworksById}
            networkLabelByKey={networkLabelByKey}
            isBusy={isSaving}
            onOpen={setOpenCampaignId}
            onOpenObjective={setOpenObjectiveId}
            onCreate={setCreatingFor}
            onCreateObjective={() => setIsCreatingObjective(true)}
          />
        ) : null
      )}

      {isCreatingObjective && (
        <ObjectiveFormModal
          isSaving={isSaving}
          onClose={() => setIsCreatingObjective(false)}
          onSubmit={handleCreateObjective}
        />
      )}

      {creatingFor && (
        <CampaignFormModal
          objectiveName={creatingFor.name}
          segments={segments}
          isSaving={isSaving}
          onClose={() => setCreatingFor(null)}
          onSubmit={handleCreate}
        />
      )}
    </>
  );
}
